import * as moment from 'moment';
import * as React from 'react';
import Input from '../../../components_v2/input/Input';
import { InputStyle } from '../../../components_v2/input/model/Model';
import type { DataType } from '../../../components_v2/table/Table';
import { DayPickerModal, PickerModalWrapperConfigProps } from '../../../components_v2/toolbarFilter/time/DatePicker';
import { DarkGreySidely2 } from '../../../styles/global/css/Utils';
import { TableRow } from '../../orders/templateOrders/style/Style';

// The date and inline-text cell editors, in their own leaf module:
// CompanyColumns reaches the report interpretor (and through it the report
// editor, which imports the company popup back), so anything under products
// must take these from here — importing CompanyColumns from there closes an
// import cycle and styled-components crashes on an undefined component.

export function DateAccessor(props: {
	date?: Date | string | null | moment.Moment,
	utc?: boolean,
	onDateChange?: (date: moment.Moment) => void,
	input?: boolean,
	inputStyle?: InputStyle,
	pickerProps?: PickerModalWrapperConfigProps
}) {
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const date = props.date ? props.utc ? moment.utc(props.date).local() : moment(props.date) : undefined;
	const value = date?.format('L - LT');
	return <>
		{props.input ? <Input disabled={!props.onDateChange} disableChange inputStyle={props.inputStyle} name={''} type='text' value={value} onClick={() => setIsOpen(true)} /> :
			<TableRow cursor={props.onDateChange ? 'pointer' : 'inherit'} onClick={() => setIsOpen(true)}>{value}</TableRow>}
		{props.onDateChange && isOpen && <DayPickerModal
			noAbsolute
			future
			onClickOut={() => setIsOpen(false)}
			time={true}
			date={date}
			onDateChange={date => {
				setIsOpen(false);
				props.onDateChange?.(date);
			}}
			{...props.pickerProps}
		/>}
	</>;
}

export function EditableContent<T>(props: {
	error?: string
	row: DataType<T> | undefined
	// When error is setted
	onUpdate?: (t: DataType<T>) => void
	valueKey: string,
	defaultValue?: string
	regex?: (s: string) => boolean
	// the value approved by regex, to push
	onValueUpdate?: (s: string | null) => void,
	onInputChange?: (s: string | null) => void,
	timeout?: number,
	style?: InputStyle,
	innerId: unknown
}) {
	const { row, error, onUpdate, valueKey, regex, onValueUpdate, defaultValue } = props;
	const [localTimeout, setLocalTimeout] = React.useState<NodeJS.Timeout>();
	const [value, setValue] = React.useState<string>(defaultValue ?? row?.[valueKey]);
	React.useEffect(() => setValue(defaultValue ?? row?.[valueKey]), [props.innerId]);

	return <Input
		placeholder="-"
		inputStyle={{
			removePlaceHolderOnFocus: true,
			backgroundColor: 'transparent',
			border: 'none',
			width: '100%',
			height: '100%',
			placeholderColor: DarkGreySidely2,
			...props.style
		}}
		name={`input-column[${valueKey}]`}
		type="text"
		value={value}
		onChange={(newValue: string) => {
			if (row) {
				props.onInputChange?.(newValue);
				setValue(newValue);
				if (regex) {
					if (localTimeout) clearTimeout(localTimeout);
					setLocalTimeout(setTimeout((newValue) => {
						const regexValue = regex(newValue);
						if (!regexValue && row.error !== (error ?? valueKey)) {
							onUpdate?.({ ...row, error: error ?? valueKey });
						} else if (regexValue && row.error === (error ?? valueKey)) {
							onUpdate?.({ ...row, error: undefined });
						}
					}, props.timeout ?? 500, newValue));
				}
			} else {
				// row-less usage (the product dimension cells): the input still
				// has to reflect what is typed
				props.onInputChange?.(newValue);
				setValue(newValue);
			}
		}}
		onBlur={_ => {
			if (localTimeout) clearTimeout(localTimeout);
			let newValue: string | null = value;
			if (!regex || (newValue != null && regex(newValue))) {
				newValue = newValue && newValue.length > 0 ? newValue : null;
				onValueUpdate?.(newValue);
			} else if (row) {
				onUpdate?.({ ...row, error: undefined });
				setValue(defaultValue ?? row[valueKey]);
			} else {
				// row-less usage: an invalid entry snaps back to the stored value
				setValue(defaultValue ?? '');
			}
		}}
	/>;
}
